// src/feedback/feedbackStorage.ts — Local-First Storage & Markdown Exporter for Website Feedback

import type { FeedbackEntry, FeedbackStatus } from './feedbackTypes';

const STORAGE_KEY = 'clawey_feedback_entries_v1';

export class FeedbackStorage {
  /**
   * Retrieve all saved feedback entries, newest first.
   */
  static loadEntries(): FeedbackEntry[] {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const list = JSON.parse(raw);
      if (!Array.isArray(list)) return [];
      return list.sort((a, b) => b.createdAt - a.createdAt);
    } catch (e) {
      console.warn('FeedbackStorage.loadEntries error:', e);
      return [];
    }
  }

  /**
   * Find a single entry by ID.
   */
  static getEntryById(id: string): FeedbackEntry | undefined {
    const all = this.loadEntries();
    return all.find((item) => item.id === id);
  }

  /**
   * Save a new feedback entry or update an existing one.
   * Auto-syncs directly to workspace feedback_inbox.json if backend/dev-server is running.
   */
  static saveEntry(entry: FeedbackEntry): void {
    try {
      const existing = this.loadEntries();
      const index = existing.findIndex((e) => e.id === entry.id);

      if (index >= 0) {
        existing[index] = entry;
      } else {
        existing.unshift(entry);
      }

      localStorage.setItem(STORAGE_KEY, JSON.stringify(existing));

      // Asynchronously sync to Antigravity workspace
      this.syncToWorkspace(entry).catch(() => {});
    } catch (e) {
      console.error('FeedbackStorage.saveEntry error:', e);
    }
  }

  /**
   * Directly syncs a feedback observation to the local repository's feedback_inbox.json.
   * Enables seamless translation from browser feedback to Antigravity features.
   */
  static async syncToWorkspace(entry: FeedbackEntry): Promise<boolean> {
    try {
      const res = await fetch('/api/feedback/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(entry),
      });
      return res.ok;
    } catch {
      return false;
    }
  }

  /**
   * Fetch latest feedback entries synced to the workspace.
   */
  static async fetchFromWorkspace(): Promise<FeedbackEntry[]> {
    try {
      const res = await fetch('/api/feedback/sync');
      if (res.ok) {
        return await res.json();
      }
      return [];
    } catch {
      return [];
    }
  }

  /**
   * Update the status of an existing feedback entry.
   */
  static updateStatus(id: string, status: FeedbackStatus): boolean {
    const entry = this.getEntryById(id);
    if (!entry) return false;
    entry.status = status;
    this.saveEntry(entry);
    return true;
  }

  /**
   * Delete an entry by ID.
   */
  static deleteEntry(id: string): boolean {
    try {
      const existing = this.loadEntries();
      const filtered = existing.filter((e) => e.id !== id);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
      return true;
    } catch (e) {
      console.error('FeedbackStorage.deleteEntry error:', e);
      return false;
    }
  }

  /**
   * Clear all entries under the current active profile.
   */
  static clearAll(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      console.error('FeedbackStorage.clearAll error:', e);
    }
  }

  /**
   * Format a feedback entry into clean GitHub-ready Markdown.
   */
  static formatAsGitHubIssue(entry: FeedbackEntry): string {
    const categoryLabels: Record<string, string> = {
      bug: '🐛 Bug',
      adhd_friction: '🧠 ADHD Ergonomics',
      curriculum: '📚 11+ Curriculum',
      feature: '✨ Feature Idea',
      praise: '⭐ Praise & Polish',
    };

    const categoryText = categoryLabels[entry.category] || entry.category.toUpperCase();

    const formattedTranscript = entry.transcript
      .map(
        (m) =>
          `**${m.role === 'assistant' ? 'Inspector Clawey' : 'User'}**: ${m.text}${
            m.image ? ' *(Visual specimen attached)*' : ''
          }`
      )
      .join('\n\n');

    return `### [${categoryText}] ${entry.title}

- **Reported At**: ${entry.dateFormatted}
- **Route / Station**: \`${entry.telemetry.route}\`
- **Severity**: **${entry.severity.toUpperCase()}**
- **Learner Profile**: \`${entry.telemetry.activeProfile}\`
- **Summary**: ${entry.summary}
${entry.suggestedAction ? `- **Suggested Remediation**: ${entry.suggestedAction}\n` : ''}
<details>
<summary>💬 Full Diagnostic Transcript (${entry.transcript.length} turns)</summary>

${formattedTranscript}

</details>

<details>
<summary>🧭 Telemetry & Device Snapshot</summary>

- **Viewport**: ${entry.telemetry.viewport.width}×${entry.telemetry.viewport.height} (DPR: ${entry.telemetry.viewport.dpr})
- **Streak Status**: ${entry.telemetry.stats.streakDays} days (${entry.telemetry.stats.isFrozen ? 'Frozen' : 'Active'})
- **Total Explorer Points (EP)**: ${entry.telemetry.stats.totalEp} EP
- **Today Practice Time**: ${entry.telemetry.stats.sessionMinutesToday} min
- **Recent Errors**: ${entry.telemetry.recentErrors.length > 0 ? entry.telemetry.recentErrors.join(', ') : 'None'}
- **User Agent**: ${entry.telemetry.userAgent || 'N/A'}

</details>
`.trim();
  }

  /**
   * Export all entries as JSON string.
   */
  static exportAllAsJson(): string {
    const all = this.loadEntries();
    return JSON.stringify(all, null, 2);
  }
}
